import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const DEFAULT_TIMEOUT_MINUTES = 60;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
];

// Routes where session timeout should NOT be enforced
const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/signup",
  "/check-email",
  "/reset-password",
  "/auth/callback",
  "/privacy",
  "/terms",
  "/cookie-policy",
  "/status",
  "/verify",
  "/unsubscribe",
  "/contact",
  "/score",
  "/high-school",
  "/how-it-works",
  "/pricing",
  "/success-stories",
  "/enterprise-solutions",
  "/enterprise/login",
  "/request-demo",
  "/case-studies",
  "/api-docs",
  "/blog",
  "/support",
  "/about-us",
  "/careers",
  "/instant-eval",
  "/connection-test",
];

const isPublicPath = (pathname: string) => {
  if (pathname === "/") return true;
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
};

export const useSessionTimeout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutMsRef = useRef<number>(DEFAULT_TIMEOUT_MINUTES * 60 * 1000);

  useEffect(() => {
    let cancelled = false;
    let cleanupFns: Array<() => void> = [];

    const signOutForInactivity = async () => {
      try {
        await supabase.auth.signOut();
      } catch (err) {
        console.error("[useSessionTimeout] signOut error:", err);
      }
      toast("You have been signed out due to inactivity");
      navigate("/login", { replace: true });
    };

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(signOutForInactivity, timeoutMsRef.current);
    };

    const setup = async () => {
      // Skip on public pages
      if (isPublicPath(location.pathname)) return;

      // Only run when authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || cancelled) return;

      // Load org's session_timeout_minutes
      try {
        const { data: memberships } = await supabase
          .from("organization_members")
          .select("organization_id")
          .eq("user_id", session.user.id)
          .limit(1)
          .maybeSingle();

        const orgId = memberships?.organization_id;
        if (orgId) {
          const { data: settings } = await supabase
            .from("organization_settings")
            .select("session_timeout_minutes")
            .eq("organization_id", orgId)
            .maybeSingle();

          const minutes =
            (settings?.session_timeout_minutes as number | null | undefined) ??
            DEFAULT_TIMEOUT_MINUTES;
          timeoutMsRef.current = Math.max(1, minutes) * 60 * 1000;
        }
      } catch (err) {
        console.warn("[useSessionTimeout] could not load timeout, using default", err);
      }

      if (cancelled) return;

      // Attach activity listeners
      const handler = () => resetTimer();
      ACTIVITY_EVENTS.forEach((evt) =>
        window.addEventListener(evt, handler, { passive: true })
      );
      cleanupFns.push(() => {
        ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handler));
      });

      resetTimer();
    };

    setup();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      cleanupFns.forEach((fn) => fn());
      cleanupFns = [];
    };
  }, [location.pathname, navigate]);
};

export default useSessionTimeout;
